/**
 * Visible video overlay for Space voice channels.
 *
 * Mirrors the Conversations call overlay (`AppCallOverlay`): a single globally
 * mounted overlay whose LiveKit room stays connected across navigation (so
 * audio persists), while the video UI is only shown when the user is viewing
 * the voice channel they joined. Reuses the shared `CallOverlayShell`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSpaces } from '../../hooks/useSpaces';
import { useVoiceChannelSession } from '../../hooks/useVoiceChannelSession';
import { CallDeviceSetupModal, type CallDeviceSelection } from './CallDeviceSetupModal';
import { CallOverlayShell } from './CallOverlayShell';
import { CallTroubleshootModal } from './CallTroubleshootModal';

export function VoiceChannelOverlay() {
  const {
    joined,
    phase,
    pendingDeviceSetup,
    livekitUrl,
    livekitToken,
    callE2EEKey,
    e2eeSupported,
    confirmVoiceDeviceSetup,
    cancelVoiceDeviceSetup,
    leaveVoiceChannel,
  } = useVoiceChannelSession();
  const { activeSpace, activeChannelId } = useSpaces();

  const [troubleshootOpen, setTroubleshootOpen] = useState(false);
  // Shared across Leave (LiveKit onDisconnected) and force-end so concurrent
  // teardown paths do not issue duplicate leave/cleanup requests.
  const leaveInFlightRef = useRef<Promise<void> | null>(null);

  // The troubleshoot modal is tied to an active call; close it once the user
  // has left so it can't linger over a channel with no call in progress.
  useEffect(() => {
    if (!joined) setTroubleshootOpen(false);
  }, [joined]);

  // Warm the LiveKit chunk as soon as a join is being set up / connected.
  useEffect(() => {
    if (
      phase === 'device-setup' ||
      phase === 'connecting' ||
      phase === 'live' ||
      (livekitUrl && livekitToken)
    ) {
      void import('./CallRoom');
    }
  }, [phase, livekitUrl, livekitToken]);

  const handleConfirmDevices = useCallback(
    async (devices: CallDeviceSelection) => {
      await confirmVoiceDeviceSetup(devices);
    },
    [confirmVoiceDeviceSetup],
  );

  const runLeaveVoiceChannel = useCallback(() => {
    if (leaveInFlightRef.current) return leaveInFlightRef.current;
    const leavePromise = leaveVoiceChannel().finally(() => {
      if (leaveInFlightRef.current === leavePromise) {
        leaveInFlightRef.current = null;
      }
    });
    leaveInFlightRef.current = leavePromise;
    return leavePromise;
  }, [leaveVoiceChannel]);

  const handleDisconnected = useCallback(() => {
    // Overlay Leave uses LiveKit DisconnectButton; mirror AppCallOverlay and
    // tear down Space presence (not only the media room).
    void runLeaveVoiceChannel();
  }, [runLeaveVoiceChannel]);

  const handleForceEnd = useCallback(async () => {
    await runLeaveVoiceChannel();
    return true;
  }, [runLeaveVoiceChannel]);

  const isViewingVoiceChannel =
    !!joined &&
    joined.channelId === activeChannelId &&
    joined.spaceId === activeSpace?.id;

  return (
    <>
      <CallDeviceSetupModal
        open={phase === 'device-setup' && pendingDeviceSetup !== null}
        variant="voice"
        onConfirm={(devices) => void handleConfirmDevices(devices)}
        onCancel={cancelVoiceDeviceSetup}
      />

      {joined && livekitUrl && livekitToken && (
        <CallOverlayShell
          variant="space"
          visible={isViewingVoiceChannel}
          phase={phase}
          serverUrl={livekitUrl}
          token={livekitToken}
          callE2EEKey={callE2EEKey}
          e2eeSupported={e2eeSupported}
          // Encrypted channels always have a derived key once connected (a
          // failed derivation blocks the join), so a missing key here means the
          // channel simply isn't encrypted rather than an encryption failure.
          e2eeExpected={!!callE2EEKey}
          streamQualityCaps={null}
          isDm={false}
          onConnected={() => {}}
          onDisconnected={handleDisconnected}
          onTroubleshoot={() => setTroubleshootOpen(true)}
        />
      )}

      <CallTroubleshootModal
        open={troubleshootOpen}
        onOpenChange={setTroubleshootOpen}
        onForceEnd={handleForceEnd}
      />
    </>
  );
}
